import { createFileRoute, Outlet, useLocation, useSearch } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui";
import { AlertCircle, BellRing, Loader2 } from "lucide-react";
import { useSelectEntry } from "../hooks/useSelectEntry";
import { PageLayout } from "../components/PageLayout";
import dayjs from "dayjs";
import { useCopy } from "../i18n/locale";

export const Route = createFileRoute("/form")({
    component: FormComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            entry_id: search.entry_id as string,
            profile_id: search.profile_id as string,
        };
    },
});

function FormComponent() {
    const { entry_id, profile_id } = useSearch({ from: "/form" });
    const location = useLocation();

    const { data: entryData, isLoading } = useSelectEntry(entry_id);
    const copy = useCopy();

    // Check if entry exists and user has permission
    const hasPermission = entryData && entryData.profile_id === profile_id;
    const permissionError = entryData && !hasPermission;

    // Check if we're on the index page (form/index.tsx)
    const isIndexPage = location.pathname === "/form" || location.pathname === "/form/";

    if (isLoading) {
        return (
            <PageLayout
                title={copy.formShell.verifyingTitle}
                description={copy.formShell.verifyingDescription}
                icon={Loader2}
                theme="blue"
                isLoading={true}
            >
                <div className="text-center animate-pulse">
                    <p className="text-slate-600 text-lg">{copy.formShell.checkingAccess}</p>
                </div>
            </PageLayout>
        );
    }

    if (!entryData) {
        return (
            <PageLayout
                title={copy.formShell.notFoundTitle}
                description={copy.formShell.notFoundDescription}
                icon={AlertCircle}
                theme="pink"
            >
                <Button onClick={() => window.location.reload()}>{copy.common.tryAgain}</Button>
            </PageLayout>
        );
    }

    if (permissionError) {
        return (
            <PageLayout
                title={copy.formShell.deniedTitle}
                description={copy.formShell.deniedDescription}
                icon={AlertCircle}
                theme="pink"
            >
                <Button onClick={() => window.location.reload()}>{copy.common.tryAgain}</Button>
            </PageLayout>
        );
    }

    const formattedDate = dayjs(entryData.created_at).format(copy.longDate);

    return (
        <PageLayout
            title={copy.formShell.title(formattedDate)}
            description={copy.formShell.description}
            icon={isIndexPage ? BellRing : undefined}
            theme="blue"
        >
            <Outlet />
        </PageLayout>
    );
}
